import { useCallback, useEffect, useRef, useState } from 'react';

export function useWebcam({ width = 640, height = 480, facingMode = 'user' } = {}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
    }
    streamRef.current = null;
    setReady(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('الكاميرا غير مدعومة في هذا المتصفح');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width, height },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      setError(err?.message || 'تعذّر الوصول إلى الكاميرا');
      setReady(false);
    }
  }, [facingMode, width, height]);

  const snapshot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !ready) return null;
    const w = video.videoWidth || width;
    const h = video.videoHeight || height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  }, [ready, width, height]);

  useEffect(() => () => stop(), [stop]);

  return { videoRef, ready, start, stop, snapshot, error };
}
